/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import {
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar
} from 'recharts';

// Custom icons using inline SVG to avoid external dependencies
const Icons = {
    Search: (props) => (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
    ),
    AngleLeft: (props) => (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
            <polyline points="15 18 9 12 15 6" />
        </svg>
    ),
    AngleRight: (props) => (
        <svg xmlns="http://www.w3.org/24/24" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
            <polyline points="9 18 15 12 9 6" />
        </svg>
    ),
    Plus: (props) => (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
            <path d="M12 5v14M5 12h14" />
        </svg>
    ),
    X: (props) => (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
    ),
    Eye: (props) => (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
            <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" />
            <circle cx="12" cy="12" r="3" />
        </svg>
    ),
    Trash: (props) => (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        </svg>
    ),
    CheckCircle: (props) => (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
            <path d="M22 11.08V12a10 10 0 1 1-5.93-8.56" />
            <path d="M22 4L12 14.01l-3-3" />
        </svg>
    ),
    Edit: (props) => (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
            <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
        </svg>
    ),
    Boxes: (props) => (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
            <path d="M21 8.5L12.5 3 4 8.5" />
            <path d="M21 15.5L12.5 21 4 15.5" />
            <path d="M21 8.5v7m-17-7v7" />
            <path d="M12.5 3v18" />
        </svg>
    ),
    ChartLine: (props) => (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
            <path d="M3 3v18h18" />
            <path d="M18.7 8.3L13 14 9 10l-6 6" />
        </svg>
    ),
    File: (props) => (
        <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-10 w-10 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
            <line x1="16" y1="13" x2="8" y2="13"></line>
            <line x1="16" y1="17" x2="8" y2="17"></line>
            <polyline points="10 9 9 9 8 9"></polyline>
        </svg>
    )
};

// Hook personnalisé pour gérer la récupération des fournisseurs pour le tableau
const useFetchSuppliers = (searchTerm, page, refreshKey) => {
    const [suppliers, setSuppliers] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const suppliersPerPage = 10;

    useEffect(() => {
        const API_URL = process.env.REACT_APP_API_URL;
        const accessToken = localStorage.getItem("accessToken");
        const headers = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
        };

        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const query = new URLSearchParams({
                    q: searchTerm,
                    page,
                    limit: suppliersPerPage,
                }).toString();

                const res = await fetch(`${API_URL}/suppliers?${query}`, { headers });
                if (!res.ok) {
                    throw new Error("Échec de la récupération des fournisseurs.");
                }
                const data = await res.json();

                const processedSuppliers = Array.isArray(data?.suppliers) ? data.suppliers.map(supplier => ({
                    ...supplier,
                })) : [];

                setSuppliers(processedSuppliers);
                setTotalCount(Number(data?.totalCount) || 0);
            } catch (err) {
                console.error("Erreur API :", err);
                setError("Échec de la récupération des fournisseurs. Veuillez réessayer plus tard.");
                setSuppliers([]);
                setTotalCount(0);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [searchTerm, page, refreshKey]);

    return { suppliers, totalCount, loading, error, suppliersPerPage };
};

// NOUVEAU HOOK : Pour récupérer et analyser les données pour les visualisations des fournisseurs
const useSupplierSummaryData = (refreshKey) => {
    const [summaryData, setSummaryData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const API_URL = process.env.REACT_APP_API_URL;
        const accessToken = localStorage.getItem("accessToken");
        const headers = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
        };

        const fetchAllSuppliers = async () => {
            setLoading(true);
            try {
                const res = await fetch(`${API_URL}/suppliers`, { headers });
                if (!res.ok) {
                    throw new Error("Échec de la récupération de toutes les données des fournisseurs.");
                }
                const data = await res.json();
                const allSuppliers = Array.isArray(data?.suppliers) ? data.suppliers : [];

                const totalSuppliers = allSuppliers.length;

                // Calcul des nouveaux fournisseurs ce mois-ci
                const now = new Date();
                const currentMonth = now.getMonth();
                const currentYear = now.getFullYear();
                const newSuppliersThisMonth = allSuppliers.filter(supplier => {
                    const supplierDate = new Date(supplier.createdAt);
                    return supplierDate.getMonth() === currentMonth && supplierDate.getFullYear() === currentYear;
                }).length;

                // Préparation des données pour le graphique des partenariats mensuels
                const monthlyPartnerships = allSuppliers.reduce((acc, supplier) => {
                    const supplierDate = new Date(supplier.createdAt);
                    const monthYear = `${supplierDate.getFullYear()}-${(supplierDate.getMonth() + 1).toString().padStart(2, '0')}`;
                    acc[monthYear] = (acc[monthYear] || 0) + 1;
                    return acc;
                }, {});

                const chartData = Object.keys(monthlyPartnerships).map(key => ({
                    name: key,
                    "Nouveaux Partenaires": monthlyPartnerships[key]
                })).sort((a, b) => a.name.localeCompare(b.name));

                setSummaryData({ totalSuppliers, newSuppliersThisMonth, chartData });
            } catch (err) {
                console.error("Erreur lors de la récupération des données de résumé :", err);
                setSummaryData(null);
            } finally {
                setLoading(false);
            }
        };

        fetchAllSuppliers();
    }, [refreshKey]);

    return { summaryData, loading };
};


// Composant de modal pour la confirmation de suppression
const DeleteSupplierModal = ({ isOpen, onClose, supplier, onConfirmDelete }) => {
    if (!isOpen || !supplier) return null;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 backdrop-filter backdrop-blur-sm flex justify-center items-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-8 text-center">
                <Icons.Trash className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-gray-900 mb-2">Êtes-vous sûr ?</h2>
                <p className="text-gray-600 mb-6">
                    Cette action est irréversible. Toutes les données de <b>{supplier.lastName}</b> seront archivées et l'utilisateur sera supprimé de manière permanente.
                </p>
                <div className="flex justify-center space-x-4">
                    <button
                        onClick={onClose}
                        className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                        Annuler
                    </button>
                    <button
                        onClick={() => onConfirmDelete(supplier.userId)}
                        className="px-6 py-3 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-colors"
                    >
                        Confirmer la suppression
                    </button>
                </div>
            </div>
        </div>
    );
};

// Composant de modal pour ajouter ou modifier un fournisseur
const SupplierFormModal = ({ isOpen, onClose, supplierToEdit, onSuccess }) => {
    const isEditing = !!supplierToEdit;
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        city: '',
        description: '',
        phoneNumber: '',
        address: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // NEW STATES FOR DOCUMENTS
    const [filesToUpload, setFilesToUpload] = useState([]);
    const [existingDocuments, setExistingDocuments] = useState([]);
    const [uploadingDocs, setUploadingDocs] = useState(false);
    const [docsError, setDocsError] = useState(null);

    const fileInputRef = useRef(null);

    // Fetch existing documents when editing
    useEffect(() => {
        const fetchDocuments = async () => {
            if (!isEditing || !supplierToEdit) return;

            setUploadingDocs(true);
            const API_URL = process.env.REACT_APP_API_URL;
            const accessToken = localStorage.getItem("accessToken");

            try {
                const res = await fetch(`${API_URL}/suppliers/${supplierToEdit.userId}/documents`, {
                    headers: { 'Authorization': `Bearer ${accessToken}` }
                });
                if (!res.ok) throw new Error("Échec de la récupération des documents existants.");

                const data = await res.json();
                setExistingDocuments(data.documents);
            } catch (err) {
                console.error("Erreur de récupération des documents:", err);
                setDocsError("Erreur lors du chargement des documents.");
            } finally {
                setUploadingDocs(false);
            }
        };

        if (isOpen) {
            setFilesToUpload([]);
            setDocsError(null);
            fetchDocuments();
            // Reset form data on modal open
            setFormData({
                firstName: supplierToEdit?.firstName || '',
                lastName: supplierToEdit?.lastName || '',
                email: supplierToEdit?.email || '',
                city: supplierToEdit?.city || '',
                description: supplierToEdit?.description || '',
                phoneNumber: supplierToEdit?.phoneNumber || '',
                address: supplierToEdit?.address || '',
            });
        }
    }, [isOpen, isEditing, supplierToEdit]);


    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // NEW: Handle file selection
    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files);
        const totalFiles = filesToUpload.length + existingDocuments.length + selectedFiles.length;

        if (totalFiles > 3) {
            setDocsError("Vous ne pouvez pas ajouter plus de 3 documents au total.");
            e.target.value = null; // Reset file input
            return;
        }

        setDocsError(null);
        setFilesToUpload(prev => [...prev, ...selectedFiles]);
    };

    // NEW: Handle file removal from the list to upload
    const handleRemoveFile = (index) => {
        setFilesToUpload(prev => prev.filter((_, i) => i !== index));
    };

    // NEW: Handle deletion of an existing document
    const handleDeleteExistingDocument = async (documentId) => {
        setUploadingDocs(true);
        const API_URL = process.env.REACT_APP_API_URL;
        const accessToken = localStorage.getItem("accessToken");
        try {
            const res = await fetch(`${API_URL}/documents/${documentId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${accessToken}` }
            });

            if (!res.ok) throw new Error("Échec de la suppression du document.");

            setExistingDocuments(prev => prev.filter(doc => doc.documentId !== documentId));
        } catch (err) {
            console.error("Erreur de suppression:", err);
            setDocsError("Erreur lors de la suppression du document.");
        } finally {
            setUploadingDocs(false);
        }
    };

    // This function is called from handleSubmit



    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const API_URL = process.env.REACT_APP_API_URL;
            const accessToken = localStorage.getItem("accessToken");

            const payload = isEditing ? { ...formData, userTypeId: 3 } : { ...formData, userTypeId: 3, password: 'password123' };
            const method = isEditing ? 'PUT' : 'POST';
            const endpoint = isEditing ? `${API_URL}/edit-user/${supplierToEdit.userId}` : `${API_URL}/post-user`;

            const res = await fetch(endpoint, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                },
                body: JSON.stringify(payload)
            });

            const data = await res.json();

            if (!res.ok) {
                const errorMessage = data.message || `Échec de ${isEditing ? 'la modification' : "l'ajout"} du fournisseur.`;
                throw new Error(errorMessage);
            }

            const supplierId = isEditing ? supplierToEdit.userId : data.userId;

            // **CORRECTED LOGIC:** Get the files directly from the ref
            const filesToUploadDirectly = fileInputRef.current.files;

            if (filesToUploadDirectly.length > 0 && supplierId) {
                await uploadDocuments(supplierId, filesToUploadDirectly);
            } else if (filesToUploadDirectly.length > 0 && !supplierId) {
                console.error("User was created successfully, but no userId was returned.");
                setError("Erreur : Le serveur n'a pas retourné l'ID du fournisseur.");
            }

            setSuccess(`Fournisseur ${isEditing ? 'modifié' : 'ajouté'} avec succès!`);
            onSuccess();
            setTimeout(() => {
                onClose();
            }, 1500);
        } catch (err) {
            console.error("Erreur API:", err);
            setError(err.message || "Une erreur est survenue lors de l'opération.");
        } finally {
            setLoading(false);
        }
    };

    const uploadDocuments = async (supplierId, files) => {
        if (files.length === 0) return;

        setUploadingDocs(true);
        const API_URL = process.env.REACT_APP_API_URL;
        const accessToken = localStorage.getItem("accessToken");

        const formData = new FormData();

        for (const file of files) { // Using a for...of loop is more robust
            formData.append('documents', file);
        }

        try {
            const res = await fetch(`${API_URL}/suppliers/${supplierId}/documents`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                },
                body: formData
            });

            if (!res.ok) {
                throw new Error("Échec du téléchargement des documents.");
            }

            setSuccess(`Documents téléchargés avec succès !`);
        } catch (err) {
            console.error("Erreur de téléchargement:", err);
            setDocsError(err.message || "Une erreur est survenue lors du téléchargement des documents.");
        } finally {
            setUploadingDocs(false);
            setFilesToUpload([]);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 backdrop-filter backdrop-blur-sm flex justify-center items-center p-4 z-50 overflow-y-auto">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-8 my-8">
                <div className="flex justify-between items-center mb-6 border-b pb-4">
                    <h2 className="text-2xl font-bold text-gray-900">{isEditing ? 'Modifier un fournisseur' : 'Ajouter un nouveau fournisseur'}</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <Icons.X className="w-6 h-6" />
                    </button>
                </div>
                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-xl mb-4 text-sm" role="alert">
                        {error}
                    </div>
                )}
                {success && (
                    <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-xl mb-4 text-sm flex items-center space-x-2" role="alert">
                        <Icons.CheckCircle className="text-green-500" />
                        <span>{success}</span>
                    </div>
                )}
                {docsError && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-xl mb-4 text-sm" role="alert">
                        {docsError}
                    </div>
                )}
                <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <input
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleFormChange}
                            placeholder="Prénom de la personne de contact"
                            required
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        />
                        <input
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleFormChange}
                            placeholder="Nom du Business"
                            required
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        />
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleFormChange}
                            placeholder="Email"
                            required
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        />
                        <input
                            type="text"
                            name="phoneNumber"
                            value={formData.phoneNumber}
                            onChange={handleFormChange}
                            placeholder="Numéro de téléphone"
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        />
                        <input
                            type="text"
                            name="address"
                            value={formData.address}
                            onChange={handleFormChange}
                            placeholder="Adresse (Quartier/Rue)"
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        />

                        {/* MODIFICATION: City devient un champ SELECT */}
                        <select
                            name="city"
                            value={formData.city}
                            onChange={handleFormChange}
                            required
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        >
                            <option value="" disabled>Sélectionner une ville *</option>
                            <option value="Kolwezi">Kolwezi</option>
                            <option value="Lubumbashi">Lubumbashi</option>
                            <option value="Kinshasa">Kinshasa</option>
                        </select>
                    </div>

                    {/* NOUVEAU: Ajout du champ description (textarea) */}
                    <div className="mt-4">
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleFormChange}
                            placeholder="Description détaillée du Business (obligatoire)"
                            required
                            rows="4"
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        ></textarea>
                    </div>

                    {/* NEW: Document Upload Section */}
                    <div className="mt-6 border-t pt-6">
                        <label className="block text-gray-700 font-semibold mb-2">
                            Documents du fournisseur (max. 3)
                        </label>
                        <input
                            type="file"
                            multiple
                            accept="image/*, .pdf"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                        />

                        {/* Document previews */}
                        <div className="mt-4 grid grid-cols-3 gap-4">
                            {/* Existing Documents */}
                            {existingDocuments.map((doc, index) => (
                                <div key={doc.documentId} className="relative group w-full h-32 rounded-lg overflow-hidden border border-gray-300 flex items-center justify-center bg-gray-100">
                                    {doc.fileType === 'image' ? (
                                        <img src={doc.documentUrl} alt={`Document ${index}`} className="object-cover w-full h-full" />
                                    ) : (
                                        <div className="text-center text-gray-500 p-2">
                                            <Icons.File />
                                            <span className="text-xs mt-1 block">PDF</span>
                                        </div>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => handleDeleteExistingDocument(doc.documentId)}
                                        className="absolute top-1 right-1 p-1 bg-red-500 rounded-full text-white hover:bg-red-600 transition-opacity opacity-0 group-hover:opacity-100"
                                        title="Supprimer ce document"
                                        disabled={uploadingDocs}
                                    >
                                        <Icons.X className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                            {/* New Files to Upload */}
                            {filesToUpload.map((file, index) => (
                                <div key={index} className="relative group w-full h-32 rounded-lg overflow-hidden border border-dashed border-gray-400 flex items-center justify-center">
                                    {file.type.startsWith('image/') ? (
                                        <img src={URL.createObjectURL(file)} alt={`Preview ${index}`} className="object-cover w-full h-full" />
                                    ) : (
                                        <div className="text-center text-gray-500 p-2">
                                            <Icons.File />
                                            <span className="text-xs mt-1 block">{file.name}</span>
                                        </div>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveFile(index)}
                                        className="absolute top-1 right-1 p-1 bg-red-500 rounded-full text-white hover:bg-red-600 transition-opacity opacity-0 group-hover:opacity-100"
                                        title="Supprimer ce fichier"
                                    >
                                        <Icons.X className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="mt-6 flex justify-end space-x-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                            Annuler
                        </button>
                        <button
                            type="submit"
                            disabled={loading || uploadingDocs}
                            className="px-6 py-3 bg-indigo-500 text-white font-semibold rounded-lg hover:bg-indigo-600 transition-colors disabled:bg-indigo-300"
                        >
                            {loading || uploadingDocs ? `Enregistrement...` : isEditing ? 'Sauvegarder les modifications' : 'Ajouter le fournisseur'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// Composant de modal pour les détails du fournisseur
const SupplierDetailsModal = ({ isOpen, onClose, supplier, onDeleteSupplier, onEditSupplier }) => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [totalRevenue, setTotalRevenue] = useState(null);
    const [supplierDocuments, setSupplierDocuments] = useState([]);

    // Combined useEffect for all data fetching
    useEffect(() => {
        const fetchSupplierData = async () => {
            if (!isOpen || !supplier || !supplier.userId) {
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(null);
            const API_URL = process.env.REACT_APP_API_URL;
            const accessToken = localStorage.getItem("accessToken");

            if (!accessToken) {
                setLoading(false);
                setError("Jeton d'authentification manquant. Veuillez vous reconnecter.");
                return;
            }

            try {
                // Fetch products and revenue in parallel
                const [productsRes, revenueRes, docsRes] = await Promise.all([
                    fetch(`${API_URL}/suppliers/${supplier.userId}/products`, { headers: { Authorization: `Bearer ${accessToken}` } }),
                    fetch(`${API_URL}/suppliers/${supplier.userId}/revenue`, { headers: { Authorization: `Bearer ${accessToken}` } }),
                    fetch(`${API_URL}/suppliers/${supplier.userId}/documents`, { headers: { 'Authorization': `Bearer ${accessToken}` } })
                ]);

                // Handle products
                if (!productsRes.ok) throw new Error("Échec de la récupération des produits.");
                const productsData = await productsRes.json();
                setProducts(Array.isArray(productsData?.products) ? productsData.products : []);

                // Handle revenue
                if (!revenueRes.ok) throw new Error("Échec de la récupération des revenus du fournisseur.");
                const revenueData = await revenueRes.json();
                setTotalRevenue(revenueData.totalRevenue);

                // Handle documents
                if (!docsRes.ok) throw new Error("Échec de la récupération des documents.");
                const docsData = await docsRes.json();
                setSupplierDocuments(docsData.documents);


            } catch (err) {
                console.error("Erreur API:", err);
                setError(err.message || "Une erreur est survenue lors de la récupération des données.");
                setSupplierDocuments([]); // Clear documents on error
                setProducts([]); // Clear products on error
                setTotalRevenue(null); // Clear revenue on error
            } finally {
                setLoading(false);
            }
        };

        fetchSupplierData();
    }, [isOpen, supplier]);

    if (!isOpen || !supplier) return null;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 backdrop-filter backdrop-blur-sm flex justify-center items-center p-4 z-50 overflow-y-auto">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl p-8 my-8">
                <div className="flex justify-between items-center mb-6 border-b pb-4">
                    <h2 className="text-2xl font-bold text-gray-900">Profile de {supplier.firstName} {supplier.lastName}</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <Icons.X className="w-6 h-6" />
                    </button>
                </div>

                {loading ? (
                    <div className="flex justify-center items-center h-48">
                        <div className="text-xl text-gray-700 animate-pulse">Chargement des données...</div>
                    </div>
                ) : error ? (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-xl text-sm" role="alert">
                        {error}
                    </div>
                ) : (
                    <div className="space-y-8">
                        {/* Détails personnels du fournisseur (existing) */}
                        <div className="bg-gray-50 p-6 rounded-xl shadow-inner">
                            <h3 className="text-xl font-semibold text-gray-800 mb-4">Informations sur l'entreprise</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-700">
                                <p><strong>Nom d'entreprise:</strong> {supplier.lastName}</p>
                                <p><strong>Personne de contact:</strong> {supplier.firstName}</p>
                                <p><strong>Email:</strong> {supplier.email}</p>
                                <p><strong>Téléphone:</strong> {supplier.phoneNumber || 'N/A'}</p>
                                <p><strong>Adresse:</strong> {supplier.address || 'N/A'}</p>
                                <p><strong>Ville:</strong> {supplier.city || 'N/A'}</p>
                                <p><strong>Partenaire depuis:</strong> {new Date(supplier.createdAt).toLocaleDateString('fr-FR')}</p>
                            </div>
                        </div>
                                <div className="border-t pt-6">
                                    <h3 className="text-xl font-semibold text-gray-800 mb-4">Description</h3>
                                    <p className="text-gray-700">{supplier.description || 'Aucune description fournie.'}</p>
                                </div>
                        {/* Documents Section (MOVED HERE) */}
                        <div className="border-t pt-6">
                            <h3 className="text-xl font-semibold text-gray-800 mb-4">Documents</h3>
                            {supplierDocuments.length > 0 ? (
                                <ul className="list-disc ml-5 space-y-2">
                                    {supplierDocuments.map(doc => (
                                        
                                        <li key={doc.documentId} className="text-blue-600 hover:underline">
                                            <a href={doc.documentUrl} target="_blank"  download={`Document_${doc.documentId}.${doc.fileType === 'image' ? 'jpg' : 'pdf'}`} rel="noreferrer">
                                                {doc.fileType === 'image' ? `Image` : `Document PDF`}
                                             
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="text-gray-500 text-sm">Aucun document n'est disponible pour ce fournisseur.</p>
                            )}
                        </div>

                        {/* NEW: Key Statistics Card */}
                        <div className="bg-white p-6 rounded-xl shadow-lg">
                            <h3 className="text-xl font-semibold text-gray-800 mb-4">Statistiques clés</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-700">
                                <div className="p-4 bg-green-50 rounded-lg">
                                    <p className="text-sm">Revenu total généré</p>
                                    <p className="text-2xl font-bold mt-1 text-green-800">
                                        CDF {parseFloat(totalRevenue).toFixed(2)}
                                    </p>
                                </div>
                                <div className="p-4 bg-blue-50 rounded-lg">
                                    <p className="text-sm">Total des produits fournis</p>
                                    <p className="text-2xl font-bold mt-1 text-blue-800">
                                        {products.length}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Tableau des produits fournis (existing) */}
                        <div className="bg-white p-6 rounded-xl shadow-lg">
                            <h3 className="text-xl font-semibold text-gray-800 mb-4">Produits fournis</h3>
                            <div className="overflow-x-auto">
                                <table className="min-w-full table-auto">
                                    <thead>
                                        <tr className="bg-gray-50 text-gray-600 uppercase text-sm font-semibold">
                                            <th className="py-3 px-6 text-left">Nom du produit</th>
                                            <th className="py-3 px-6 text-left">Catégorie</th>
                                            <th className="py-3 px-6 text-left">Prix</th>
                                            <th className="py-3 px-6 text-left">Stock</th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-gray-600 text-sm font-light divide-y divide-gray-200">
                                        {products.length > 0 ? (
                                            products.map(product => (
                                                <tr key={product.productId} className="hover:bg-gray-50 transition-colors">
                                                    <td className="py-3 px-6 whitespace-nowrap">{product.productName}</td>
                                                    <td className="py-3 px-6">{product.categoryName}</td>
                                                    <td className="py-3 px-6">CDF {parseFloat(product.price).toFixed(2)}/{product.unit}</td>
                                                    <td className="py-3 px-6">{product.stockQuantity}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="4" className="py-10 text-center text-gray-500">Aucun produit trouvé pour ce fournisseur.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
                <div className="mt-8 pt-4 border-t flex justify-between">
                    <button
                        onClick={() => onEditSupplier(supplier)}
                        className="flex items-center space-x-2 px-6 py-3 bg-blue-500 text-white font-semibold rounded-xl hover:bg-blue-600 transition-colors"
                    >
                        <Icons.Edit />
                        <span>Modifier le fournisseur</span>
                    </button>
                    <button
                        onClick={() => onDeleteSupplier(supplier)}
                        className="flex items-center space-x-2 px-6 py-3 bg-red-500 text-white font-semibold rounded-xl hover:bg-red-600 transition-colors"
                    >
                        <Icons.Trash />
                        <span>Supprimer le fournisseur</span>
                    </button>

                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <Icons.X className="w-6 h-6" />
                    </button>
                </div>
            </div>
        </div>
    );
};

// Composant principal de la page Fournisseurs
const SuppliersPage = () => {
    const [inputTerm, setInputTerm] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const [statusMessage, setStatusMessage] = useState(null);
    const [messageType, setMessageType] = useState('success');

    const { suppliers, totalCount, loading, suppliersPerPage } = useFetchSuppliers(searchTerm, page, refreshKey);
    const { summaryData, loading: summaryLoading } = useSupplierSummaryData(refreshKey);

    const totalPages = Math.ceil(totalCount / suppliersPerPage);

    const showMessage = (message, type = 'success') => {
        setStatusMessage(message);
        setMessageType(type);
        setTimeout(() => setStatusMessage(null), 5000);
    };

    const handleInputChange = (e) => {
        setInputTerm(e.target.value);
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setSearchTerm(inputTerm);
        setPage(1);
    };

    const handleSuccess = () => {
        setRefreshKey(prev => prev + 1);
        showMessage(`Opération réussie!`);
    };

    const handleViewSupplier = (supplier) => {
        setSelectedSupplier(supplier);
        setIsViewModalOpen(true);
    };

    const handleEditSupplier = (supplier) => {
        setSelectedSupplier(supplier);
        setIsViewModalOpen(false);
        setIsEditModalOpen(true);
    };

    const handleDeleteSupplier = (supplier) => {
        setSelectedSupplier(supplier);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = async (userId) => {
        try {
            const API_URL = process.env.REACT_APP_API_URL;
            const accessToken = localStorage.getItem("accessToken");

            const res = await fetch(`${API_URL}/delete-user/${userId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            });

            if (!res.ok) {
                const errorData = await res.json();
                throw new Error(errorData.message || "Échec de la suppression du fournisseur.");
            }

            setIsDeleteModalOpen(false);
            setIsViewModalOpen(false);
            setRefreshKey(prev => prev + 1);
            showMessage("Fournisseur supprimé avec succès !");

        } catch (err) {
            console.error("Erreur lors de la suppression:", err);
            showMessage(err.message || "Une erreur est survenue lors de la suppression.", 'error');
        }
    };

    if (loading || summaryLoading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="text-2xl text-gray-700 animate-pulse">Chargement des données...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100 font-sans p-8 text-gray-900">
            <div className="max-w-8xl mx-auto">
                <header className="mb-8 flex justify-between items-center">
                    <div>
                        <h1 className="text-4xl font-extrabold text-gray-900">
                            Gestion des fournisseurs 📦
                        </h1>
                        <p className="mt-2 text-lg text-gray-500">
                            Visualisez et recherchez les fournisseurs de votre plateforme.
                        </p>
                    </div>
                    <button
                        onClick={() => setIsAddModalOpen(true)}
                        className="flex items-center space-x-2 px-6 py-3 bg-green-500 text-white font-semibold rounded-xl hover:bg-green-600 transition-colors"
                    >
                        <Icons.Plus />
                        <span>Ajouter un fournisseur</span>
                    </button>
                </header>

                {statusMessage && (
                    <div className={`border px-4 py-3 rounded-xl mb-4 text-sm flex items-center space-x-2 ${messageType === 'success' ? 'bg-green-100 border-green-400 text-green-700' : 'bg-red-100 border-red-400 text-red-700'}`} role="alert">
                        <Icons.CheckCircle className="text-green-500" />
                        <span>{statusMessage}</span>
                    </div>
                )}

                {summaryData && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                        {/* Carte 1 : Total fournisseurs */}
                        <div className="bg-white p-6 rounded-xl shadow-lg flex items-center space-x-4">
                            <div className="p-4 bg-indigo-100 text-indigo-600 rounded-full">
                                <Icons.Boxes className="w-6 h-6" />
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold text-gray-500 uppercase">Total Fournisseurs</h3>
                                <p className="text-3xl font-bold text-gray-900">{summaryData.totalSuppliers}</p>
                            </div>
                        </div>

                        {/* Carte 2 : Nouveaux ce mois-ci */}
                        <div className="bg-white p-6 rounded-xl shadow-lg flex items-center space-x-4">
                            <div className="p-4 bg-green-100 text-green-600 rounded-full">
                                <Icons.Plus className="w-6 h-6" />
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold text-gray-500 uppercase">Nouveaux ce mois-ci</h3>
                                <p className="text-3xl font-bold text-gray-900">{summaryData.newSuppliersThisMonth}</p>
                            </div>
                        </div>

                        {/* Carte 3 : Taux de croissance */}
                        <div className="bg-white p-6 rounded-xl shadow-lg flex items-center space-x-4">
                            <div className="p-4 bg-yellow-100 text-yellow-600 rounded-full">
                                <Icons.ChartLine className="w-6 h-6" />
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold text-gray-500 uppercase">Croissance</h3>
                                <p className="text-3xl font-bold text-gray-900">
                                    +{((summaryData.newSuppliersThisMonth / (summaryData.totalSuppliers - summaryData.newSuppliersThisMonth)) * 100).toFixed(2) || 0}%
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {summaryData && (
                    <div className="bg-white bg-opacity-80 backdrop-filter backdrop-blur-lg p-6 rounded-xl shadow-lg mb-6">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">Partenariats mensuels</h2>
                        <ResponsiveContainer width="100%" height={250}>
                            <BarChart data={summaryData.chartData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis dataKey="name" tickFormatter={(tick) => tick.slice(5)} />
                                <YAxis />
                                <Tooltip />
                                <Bar dataKey="Nouveaux Partenaires" fill="#6366f1" radius={[10, 10, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}

                <div className="bg-white bg-opacity-80 backdrop-filter backdrop-blur-lg p-6 rounded-xl shadow-lg mb-6">
                    <form onSubmit={handleSearch}>
                        <div className="flex items-center space-x-4 mb-6">
                            <div className="relative flex-1">
                                <Icons.Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    value={inputTerm}
                                    onChange={handleInputChange}
                                    placeholder="Rechercher par nom d'entreprise ou email..."
                                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 transition-all duration-200"
                                />
                            </div>
                            <button
                                type="submit"
                                className="px-8 py-3 bg-indigo-500 text-white font-semibold rounded-xl hover:bg-indigo-600 transition-colors"
                            >
                                Rechercher
                            </button>
                        </div>
                    </form>
                    <div className="overflow-x-auto">
                        <table className="min-w-full table-auto">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-200 text-gray-600 uppercase text-sm font-semibold">
                                    <th className="py-4 px-6 text-left rounded-tl-xl">Nom d'entreprise</th>
                                    <th className="py-4 px-6 text-left">Personne de contact</th>
                                    <th className="py-4 px-6 text-left">Email</th>
                                    <th className="py-4 px-6 text-left">Ville</th>
                                    <th className="py-4 px-6 text-left">Téléphone</th>
                                    <th className="py-4 px-6 text-left">Date de partenariat</th>
                                    <th className="py-4 px-6 text-center rounded-tr-xl">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="text-gray-600 text-sm font-light divide-y divide-gray-200">
                                {suppliers.length > 0 ? (
                                    suppliers.map((supplier) => (
                                        <tr key={supplier.userId} className="hover:bg-gray-50 transition-colors duration-200">
                                            <td className="py-4 px-6 text-left whitespace-nowrap">
                                                <div className="flex items-center space-x-3">
                                                    <Icons.Boxes className="w-5 h-5 text-gray-500" />
                                                    <span className="font-medium text-gray-800">
                                                        {supplier.lastName || ''}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="py-4 px-6 text-left">
                                                <span className="text-gray-500 font-medium">
                                                    {supplier.firstName || 'N/A'}
                                                </span>
                                            </td>
                                            <td className="py-4 px-6 text-left">
                                                <span className="text-blue-600 font-medium">
                                                    {supplier.email || 'N/A'}
                                                </span>
                                            </td>
                                            <td className="py-4 px-6 text-left">
                                                <span className="text-gray-500 font-medium">
                                                    {supplier.city || 'N/A'}
                                                </span>
                                            </td>
                                            <td className="py-4 px-6 text-left">
                                                <span className="text-gray-500">
                                                    {supplier.phoneNumber || 'N/A'}
                                                </span>
                                            </td>
                                            <td className="py-4 px-6 text-left">
                                                <span className="text-gray-500 text-sm">
                                                    {supplier.createdAt ? new Date(supplier.createdAt).toLocaleDateString('fr-FR') : 'N/A'}
                                                </span>
                                            </td>
                                            <td className="py-4 px-6 text-center">
                                                <div className="flex justify-center space-x-2">
                                                    <button
                                                        onClick={() => handleViewSupplier(supplier)}
                                                        className="p-2 rounded-full text-indigo-500 hover:bg-indigo-100 transition-colors"
                                                        title="Voir les détails"
                                                    >
                                                        <Icons.Eye className="w-5 h-5" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteSupplier(supplier)}
                                                        className="p-2 rounded-full text-red-500 hover:bg-red-100 transition-colors"
                                                        title="Supprimer le fournisseur"
                                                    >
                                                        <Icons.Trash className="w-5 h-5" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="py-10 text-center text-gray-500">
                                            Aucun fournisseur trouvé.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="flex justify-between items-center mt-6">
                        <div className="text-sm text-gray-600">
                            Affichage de {(page - 1) * suppliersPerPage + 1} à {Math.min(page * suppliersPerPage, totalCount)} sur {totalCount} fournisseurs
                        </div>
                        <div className="flex space-x-2">
                            <button
                                onClick={() => setPage(page - 1)}
                                disabled={page === 1}
                                className="p-3 rounded-full bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                <Icons.AngleLeft className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => setPage(page + 1)}
                                disabled={page >= totalPages}
                                className="p-3 rounded-full bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                <Icons.AngleRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <SupplierFormModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                onSuccess={handleSuccess}
                supplierToEdit={null}
            />
            <SupplierFormModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                onSuccess={handleSuccess}
                supplierToEdit={selectedSupplier}
            />
            <SupplierDetailsModal
                isOpen={isViewModalOpen}
                onClose={() => setIsViewModalOpen(false)}
                supplier={selectedSupplier}
                onDeleteSupplier={handleDeleteSupplier}
                onEditSupplier={handleEditSupplier}

            />
            <DeleteSupplierModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                supplier={selectedSupplier}
                onConfirmDelete={handleConfirmDelete}
            />
        </div>
    );
};

export default SuppliersPage;